import { Avatar, Divider } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useMemo, useState } from 'react';
import { useMatch } from 'react-router';

import {
  BaseThreadFragmentFragmentGQLType,
  GetEventLogsQueryGQLType,
  ThreadActionGQLType,
  FundingRequestEntityMetaFragmentFragmentGQLType,
  EventLogTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { PermissionsFn } from '~/core/PermissionGuard';
import * as CreditFacilityEntityConfig from '~/core/routes/credit-facilities/EntityConfig';
import { Thread } from '~/packages/comments/Thread';
import { FinleyLogo } from '~/packages/common-frontend/components/FinleyLogo';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { UserDetail } from '~/packages/common-frontend/components/UserDetail';
import { COLORS_BRAND } from '~/packages/common-frontend/theme/tokens';
import { EntityName as BaseEntityName } from '~/packages/event-logs/EntityName';
import { EventFeedItem as BaseEventFeedItem } from '~/packages/event-logs/EventFeedItem';
import {
  EventLogFilter,
  useEventLogFilters,
} from '~/packages/event-logs/EventLogFilters';
import { basicStringSearch } from '~/packages/event-logs/helpers';
import { useGetFundingRequestFiltersQuery } from '~/packages/event-logs/queries/entityFilters.gql.generated';
import { EventLogExportDataRow } from '~/packages/event-logs/types';

// Constants
export const EVENT_LOG_TYPES: EventLogTypeGQLType[] = [
  'fundingRequest_request_approved',
  'fundingRequest_request_canceled',
  'fundingRequest_request_expired',
  'fundingRequest_request_pending',
  'fundingRequest_request_rejected',
  'fundingRequest_request_submitted',
  'fundingRequest_request_thread_create',
  'fundingRequest_request_unsubmitted',
];

// Types
export type EntityMeta = FundingRequestEntityMetaFragmentFragmentGQLType;
type EventLogData = GetEventLogsQueryGQLType['eventLogs']['items'][number];
type ThreadData = BaseThreadFragmentFragmentGQLType;

// Exports
export const permissions: PermissionsFn = (context) => {
  if (context.user.organization.slug === 'valleyBank') return 'deny';
  return context.flags.productsFundingRequestsPermissions;
};

type EntityNameProps = Pick<EntityMeta, 'fundingRequestId' | 'status'>;
export const EntityName = ({ fundingRequestId, status }: EntityNameProps) => {
  const match = useMatch({
    path: '/funding-requests/:fundingRequestId',
    end: true,
  });

  const isOnFundingRequestRoute =
    match?.params.fundingRequestId === fundingRequestId;

  return (
    <BaseEntityName
      isOnEntityRoute={status !== 'expired' && isOnFundingRequestRoute}
      label={`Funding Request ...${fundingRequestId.slice(-12)}`}
      tooltipLabel={`Funding Request ID: ${fundingRequestId}`}
      to={`/funding-requests/${fundingRequestId}`}
    />
  );
};

type ThreadFeedItemProps = {
  thread: ThreadData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const ThreadFeedItem = ({
  thread,
  onThreadAction,
  onMount,
  onReady,
}: ThreadFeedItemProps) => {
  switch (thread.domain) {
    case 'fundingRequest_request': {
      return (
        <Thread
          key={thread.threadId}
          threadId={thread.threadId}
          onThreadAction={onThreadAction}
          onReady={() => onReady?.(thread.threadId)}
          onMount={() => onMount?.(thread.threadId)}
        >
          <Typography inline variant="detail-muted">
            Thread on{' '}
            <EntityName
              fundingRequestId={thread.domainIdentifier}
              // !TODO: switch to entity metadata
              // status={thread.entityMetadata.status}
              status="pending"
            />{' '}
            {/* // !TODO: switch to entity metadata */}
            {/* for{' '} */}
            {/* <CreditFacilityEntityConfig.EntityName {...thread.creditFacility} /> */}
          </Typography>
          <Divider />
        </Thread>
      );
    }
    default: {
      return null;
    }
  }
};

type EventFeedItemProps = {
  log: EventLogData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const EventFeedItem = ({
  log,
  onThreadAction,
  onMount,
  onReady,
}: EventFeedItemProps) => {
  switch (log.__typename) {
    case 'FundingRequestThreadCreateEvent': {
      return (
        <Thread
          key={log.eventId}
          threadId={log.current.id}
          onThreadAction={onThreadAction}
          onMount={() => onMount?.(log.current.id)}
          onReady={() => onReady?.(log.current.id)}
        >
          <Typography inline variant="detail-muted">
            Thread on <EntityName {...log.entityMetadata} /> for{' '}
            <CreditFacilityEntityConfig.EntityName {...log.creditFacility} />
          </Typography>
          <Divider />
        </Thread>
      );
    }

    case 'FundingRequestCreatedEvent': {
      return (
        <BaseEventFeedItem
          key={log.eventId}
          createdAt={log.createdAt}
          leftSlot={<UserDetail variant="avatar" user={log.actor} />}
        >
          <UserDetail variant="name" user={log.actor} /> created{' '}
          <EntityName {...log.entityMetadata} /> for{' '}
          <CreditFacilityEntityConfig.EntityName {...log.creditFacility} />
        </BaseEventFeedItem>
      );
    }

    case 'FundingRequestStatusChangeEvent': {
      const drawAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: 'narrowSymbol',
      }).format(log.current.drawRequestTotalAmount);

      // Different styling for expired requests, user icon is replaced with Finley logo
      if (log.current.status === 'expired') {
        return (
          <BaseEventFeedItem
            key={log.eventId}
            createdAt={log.createdAt}
            leftSlot={
              <Avatar
                size="xs"
                background={COLORS_BRAND.poppy[400]}
                flexShrink={0}
                icon={<FinleyLogo colorScheme="monochrome" />}
                zIndex="1"
                userSelect="none"
              />
            }
          >
            <EntityName {...log.entityMetadata} /> for{' '}
            <CreditFacilityEntityConfig.EntityName {...log.creditFacility} />{' '}
            expired.
          </BaseEventFeedItem>
        );
      }
      return (
        <BaseEventFeedItem
          key={log.eventId}
          createdAt={log.createdAt}
          leftSlot={<UserDetail variant="avatar" user={log.actor} />}
        >
          <UserDetail variant="name" user={log.actor} /> {log.current.status}{' '}
          <EntityName {...log.entityMetadata} /> for{' '}
          <CreditFacilityEntityConfig.EntityName {...log.creditFacility} /> to
          draw {drawAmount}
        </BaseEventFeedItem>
      );
    }

    default: {
      return null;
    }
  }
};

export const EntityFilter = () => {
  const selectedIds = useEventLogFilters((s) => s.fundingRequestIds);
  const onSelect = useEventLogFilters((s) => s.setFundingRequestIds);

  const [searchString, setSearchString] = useState<string>('');

  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = useMemo(
    () => facilities.map((facility) => facility.id),
    [facilities],
  );

  const entityFiltersQuery = useGetFundingRequestFiltersQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      creditFacilityIds,
      // !TODO if we want more, we should allow users to search for entity by string
      limit: 25,
    },
  });

  const entities: EntityMeta[] = useMemo(
    () =>
      entityFiltersQuery.data?.fundingRequests.items.map((entity) => ({
        ...entity,
        __typename: 'FundingRequestEntityMeta',
        creditFacilityId: entity.creditFacility.id,
        creditFacilityName: entity.creditFacility.name,
        // Don't need these as filters at the moment, can update as needed
        loanNames: [],
        externalLoanIds: [],
      })) ?? [],
    [entityFiltersQuery.data?.fundingRequests.items],
  );

  const options = useMemo(
    () =>
      entities
        .map((entity) => ({
          id: entity.fundingRequestId,
          textLabel: `Funding Request ...${entity.fundingRequestId.slice(-12)}`,
          data: entity,
        }))
        .filter((option) => basicStringSearch(searchString, option.textLabel)),
    [entities, searchString],
  );

  if (entityFiltersQuery.error) {
    Sentry.captureException(entityFiltersQuery.error);
    return null;
  }

  if (entities.length < 1) return null;

  return (
    <EventLogFilter
      onSearchInputChange={setSearchString}
      leftIcon={<Icon icon="dollar-sign" />}
      minWidth="14rem"
      noneLabel="Any Funding Request"
      singularLabel="Funding Request"
      pluralLabel="Funding Requests"
      selectedIds={selectedIds}
      options={options}
      onClose={onSelect}
    />
  );
};

/* eslint-disable camelcase */
export const toExportDataRow = (
  dataRow: EventLogExportDataRow,
  log: EventLogData,
): EventLogExportDataRow => {
  switch (log.__typename) {
    case 'FundingRequestThreadCreateEvent': {
      return {
        ...dataRow,
        fundingRequest_meta_expire: new Date(
          log.entityMetadata.expire,
        ).toISOString(),
        fundingRequest_meta_status: log.entityMetadata.status,
        loan_names: log.entityMetadata.loanNames.join(', '),
        loan_externalIds: log.entityMetadata.externalLoanIds.join(', '),
      };
    }

    case 'FundingRequestCreatedEvent':
    case 'FundingRequestStatusChangeEvent': {
      return {
        ...dataRow,
        fundingRequest_meta_expire: new Date(
          log.entityMetadata.expire,
        ).toISOString(),
        fundingRequest_meta_status: log.entityMetadata.status,
        fundingRequest_status_drawRequestTotalAmount:
          log.current.drawRequestTotalAmount,
        fundingRequest_status_collectionsPaydownTotalAmount:
          log.current.collectionsPaydownTotalAmount,
        fundingRequest_status_collectionsRecycleTotalAmount:
          log.current.collectionsRecycleTotalAmount,
        fundingRequest_status_curePaydownTotalAmount:
          log.current.curePaydownTotalAmount,
        loan_names: log.entityMetadata.loanNames.join(', '),
        loan_externalIds: log.entityMetadata.externalLoanIds.join(', '),
      };
    }

    default: {
      return dataRow;
    }
  }
};
/* eslint-enable camelcase */
